import { useState } from 'react';
import { AgentPageShell, FormCard, Field, inputCls } from '../../components/AgentPageShell';
import { AgentResult } from '../../components/AgentResult';
import { api, ApiError } from '../../lib/api';
import { formatMillimes, tndStringToMillimes } from '../../lib/money';
import type { InvokeResponseBase } from '../../lib/types';

interface ScenarioForm {
  scenario_id: string;
  label: string;
  delta_tnd: string;
  sector_override: string;
  rationale: string;
}

interface OptimisateurResult {
  fiscal_year: number;
  baseline: { taxable_income_millimes: number; is_due_millimes: number; rate_applied: number };
  scenarios: Array<{
    scenario_id: string;
    label: string;
    rationale: string;
    taxable_income_millimes: number;
    is_due_millimes: number;
    rate_applied: number;
    savings_vs_baseline_millimes: number;
    savings_pct: number;
  }>;
  best_scenario_id: string;
  total_potential_savings_millimes: number;
}

const SAMPLES: ScenarioForm[] = [
  {
    scenario_id: 'S1',
    label: 'Investissement R&D + 10K dotation',
    delta_tnd: '-10000.000',
    sector_override: '',
    rationale: "Crédit d'impôt R&D selon Loi de Finances 2026",
  },
  {
    scenario_id: 'S2',
    label: 'Reclassement industrie export',
    delta_tnd: '0.000',
    sector_override: 'industrie_export',
    rationale: 'Activité éligible au régime industriel exportateur (10%)',
  },
];

export function OptimisateurPage() {
  const [fiscalYear, setFiscalYear] = useState(2026);
  const [start, setStart] = useState('2026-01-01');
  const [end, setEnd] = useState('2026-12-31');
  const [baselineIncomeTnd, setBaselineIncomeTnd] = useState('100000.000');
  const [baselineSector, setBaselineSector] = useState('');
  const [scenarios, setScenarios] = useState<ScenarioForm[]>(SAMPLES);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [response, setResponse] = useState<InvokeResponseBase | null>(null);

  function update(i: number, patch: Partial<ScenarioForm>) {
    setScenarios((p) => p.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));
  }
  function add() {
    setScenarios((p) => [
      ...p,
      { scenario_id: `S${p.length + 1}`, label: '', delta_tnd: '0.000', sector_override: '', rationale: '' },
    ]);
  }
  function remove(i: number) {
    setScenarios((p) => p.filter((_, idx) => idx !== i));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResponse(null);
    try {
      const payload = {
        fiscal_year: fiscalYear,
        fiscal_year_start: start,
        fiscal_year_end: end,
        baseline_taxable_income_millimes: tndStringToMillimes(baselineIncomeTnd),
        ...(baselineSector ? { baseline_sector_code: baselineSector } : {}),
        scenarios: scenarios.map((s) => ({
          scenario_id: s.scenario_id,
          label: s.label,
          taxable_income_delta_millimes: tndStringToMillimes(s.delta_tnd),
          ...(s.sector_override ? { sector_code_override: s.sector_override } : {}),
          rationale: s.rationale,
        })),
      };
      const res = await api.invokeAgent('optimisateur', { payload, fiscal_reference_date: end });
      setResponse(res);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : (err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <AgentPageShell
      title="Optimisateur"
      module="Fiscal · Phase 3"
      model="Opus 4.7"
      description={
        <>
          Analyse what-if comparant des scénarios d'optimisation IS — dotations supplémentaires,
          reclassements sectoriels, crédits d'impôt. Recalcule l'IS via engine-is pour chaque variante
          et identifie le scénario optimal.
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <FormCard title="Baseline (situation actuelle)">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <Field label="Année fiscale"><input type="number" className={inputCls} value={fiscalYear} onChange={(e) => setFiscalYear(parseInt(e.target.value || '2026', 10))} /></Field>
            <Field label="Début exercice"><input type="date" className={inputCls} value={start} onChange={(e) => setStart(e.target.value)} required /></Field>
            <Field label="Fin exercice"><input type="date" className={inputCls} value={end} onChange={(e) => setEnd(e.target.value)} required /></Field>
            <Field label="Bénéfice imposable (TND)"><input className={inputCls + ' font-mono'} value={baselineIncomeTnd} onChange={(e) => setBaselineIncomeTnd(e.target.value)} required /></Field>
            <Field label="Secteur baseline">
              <select className={inputCls} value={baselineSector} onChange={(e) => setBaselineSector(e.target.value)}>
                <option value="">Standard (15%)</option>
                <option value="industrie_export">Industrie export (10%)</option>
                <option value="banque_assurance">Banque/Assurance (25%)</option>
                <option value="hydrocarbures">Hydrocarbures (35%)</option>
              </select>
            </Field>
          </div>
        </FormCard>

        <FormCard
          title={`Scénarios à comparer (${scenarios.length})`}
          action={<button type="button" onClick={add} className="text-xs px-2 py-1 rounded bg-ink text-cream hover:opacity-90">+ Ajouter scénario</button>}
        >
          <div className="space-y-3">
            {scenarios.map((s, idx) => (
              <div key={idx} className="border border-border rounded p-3 bg-cream/30 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-2 items-end">
                <Field label="ID"><input className={inputCls + ' font-mono'} value={s.scenario_id} onChange={(e) => update(idx, { scenario_id: e.target.value })} required /></Field>
                <Field label="Libellé"><input className={inputCls} value={s.label} onChange={(e) => update(idx, { label: e.target.value })} required /></Field>
                <Field label="Δ Bénéfice (TND)" hint="Ex. -10000 pour réduction"><input className={inputCls + ' font-mono'} value={s.delta_tnd} onChange={(e) => update(idx, { delta_tnd: e.target.value })} /></Field>
                <Field label="Secteur cible">
                  <select className={inputCls} value={s.sector_override} onChange={(e) => update(idx, { sector_override: e.target.value })}>
                    <option value="">— inchangé —</option>
                    <option value="industrie_export">Industrie export</option>
                    <option value="banque_assurance">Banque/Assurance</option>
                    <option value="hydrocarbures">Hydrocarbures</option>
                  </select>
                </Field>
                <Field label="Justification"><input className={inputCls} value={s.rationale} onChange={(e) => update(idx, { rationale: e.target.value })} required /></Field>
                {scenarios.length > 1 && (
                  <button type="button" onClick={() => remove(idx)} className="text-xs text-red-700 hover:text-red-900 self-end">Supprimer</button>
                )}
              </div>
            ))}
          </div>
        </FormCard>

        <button type="submit" disabled={loading || scenarios.length === 0} className="btn-primary">
          {loading ? 'Analyse what-if…' : 'Comparer les scénarios'}
        </button>
      </form>

      {error && <div className="card border border-red-300 bg-red-50 text-red-900 text-sm">{error}</div>}

      {response && (
        <AgentResult
          response={response}
          renderResult={(result) => <OptimisateurResultView result={result as OptimisateurResult} />}
        />
      )}
    </AgentPageShell>
  );
}

function OptimisateurResultView({ result }: { result: OptimisateurResult }) {
  const sortedByImpact = [...result.scenarios].sort((a, b) => b.savings_vs_baseline_millimes - a.savings_vs_baseline_millimes);
  return (
    <div className="space-y-5">
      <section>
        <div className="text-xs uppercase tracking-wider text-taupe mb-2">Baseline</div>
        <div className="grid grid-cols-3 gap-2 text-sm">
          <Stat label="Bénéfice imposable" value={formatMillimes(result.baseline.taxable_income_millimes)} />
          <Stat label="IS dû (baseline)" value={formatMillimes(result.baseline.is_due_millimes)} />
          <Stat label="Taux appliqué" value={`${(result.baseline.rate_applied * 100).toFixed(2)}%`} />
        </div>
      </section>

      <section>
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <div className="text-xs uppercase tracking-wider text-taupe">Comparatif des scénarios</div>
          <div className="text-sm">
            <span className="px-2 py-1 rounded bg-emerald-100 text-emerald-900 border border-emerald-300 text-xs">
              ★ Meilleur : <strong>{result.best_scenario_id}</strong>
            </span>
            <span className="ml-2 text-xs text-taupe">économie potentielle totale : <strong>{formatMillimes(result.total_potential_savings_millimes)}</strong></span>
          </div>
        </div>
        <div className="overflow-x-auto -mx-2">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-wider text-taupe border-b border-border">
                <th className="px-2 py-2 text-left">ID</th>
                <th className="px-2 py-2 text-left">Scénario</th>
                <th className="px-2 py-2 text-right">Bénéfice imp.</th>
                <th className="px-2 py-2 text-right">IS dû</th>
                <th className="px-2 py-2 text-right">Taux</th>
                <th className="px-2 py-2 text-right">Économie</th>
                <th className="px-2 py-2 text-right">Économie %</th>
              </tr>
            </thead>
            <tbody>
              {sortedByImpact.map((s) => {
                const isBest = s.scenario_id === result.best_scenario_id;
                return (
                  <tr key={s.scenario_id} className={`border-b border-border/50 ${isBest ? 'bg-emerald-50/40' : ''}`}>
                    <td className="px-2 py-1.5 font-mono">
                      {isBest && '★ '}
                      {s.scenario_id}
                    </td>
                    <td className="px-2 py-1.5">
                      <div>{s.label}</div>
                      <div className="text-xs text-taupe italic">{s.rationale}</div>
                    </td>
                    <td className="px-2 py-1.5 text-right font-mono">{formatMillimes(s.taxable_income_millimes, { withSymbol: false })}</td>
                    <td className="px-2 py-1.5 text-right font-mono">{formatMillimes(s.is_due_millimes, { withSymbol: false })}</td>
                    <td className="px-2 py-1.5 text-right font-mono text-xs">{(s.rate_applied * 100).toFixed(2)}%</td>
                    <td className={`px-2 py-1.5 text-right font-mono font-bold ${s.savings_vs_baseline_millimes > 0 ? 'text-emerald-800' : s.savings_vs_baseline_millimes < 0 ? 'text-rose-800' : ''}`}>
                      {s.savings_vs_baseline_millimes > 0 ? '+' : ''}{formatMillimes(s.savings_vs_baseline_millimes, { withSymbol: false })}
                    </td>
                    <td className="px-2 py-1.5 text-right font-mono text-xs">{s.savings_pct >= 0 ? '+' : ''}{s.savings_pct.toFixed(2)}%</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="p-3 rounded border border-border bg-white text-center">
      <div className="text-xs uppercase tracking-wider text-taupe">{label}</div>
      <div className="font-mono text-lg">{value}</div>
    </div>
  );
}
